import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { PasswordGate } from '../components/PasswordGate';
import { Auth as AuthComponent } from '../components/Auth';
import { UserService } from '../services/supabase';

interface AuthPageProps {
  onAuthSuccess: (user: any) => void;
}

export default function AuthPage({ onAuthSuccess }: AuthPageProps) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [passwordGatePassed, setPasswordGatePassed] = useState(false);
  const [signUpError, setSignUpError] = useState<string | null>(null);
  const action = searchParams.get('action') || 'login';

  useEffect(() => {
    // Check if password gate was already passed in this session
    const gatePassed = sessionStorage.getItem('password_gate_passed') === 'true';
    if (gatePassed) {
      setPasswordGatePassed(true);
    }
  }, []);

  const handlePasswordGateSuccess = () => {
    setPasswordGatePassed(true);
  };

  const handleSignUp = async (email: string, password: string) => {
    const userService = new UserService();
    const { error } = await userService.signUp(email, password);
    if (error) throw error;
    sessionStorage.removeItem('supabase_invite_detected');
    navigate(`/verify-email?reason=signup&email=${encodeURIComponent(email)}`, { replace: true });
  };

  if (!passwordGatePassed) {
    return <PasswordGate onSuccess={handlePasswordGateSuccess} />;
  }

  // Show appropriate auth form based on action
  if (action === 'signup') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="glass-card rounded-xl p-8 shadow-lg">
            {signUpError && (
              <div className="mb-4 p-4 rounded-lg border bg-destructive/10 border-destructive/20 text-destructive text-sm">
                {signUpError}
              </div>
            )}
            <div className="text-center mb-8">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-primary shadow-glow flex items-center justify-center">
                <img
                  src="/logos/pink_dolphin_logo.png"
                  alt="Pink Dolphin logo"
                  className="h-10 w-10 object-contain"
                />
              </div>
              <h1 className="text-2xl font-bold text-gradient mb-2">Create Account</h1>
              <p className="text-muted-foreground">Start your 90-day free trial</p>
            </div>

            <form
              onSubmit={async (e) => {
                e.preventDefault();
                const formData = new FormData(e.currentTarget);
                const email = formData.get('email') as string;
                const password = formData.get('password') as string;
                const confirmPassword = formData.get('confirmPassword') as string;

                if (password !== confirmPassword) {
                  setSignUpError('Passwords do not match');
                  return;
                }

                if (password.length < 6) {
                  setSignUpError('Password must be at least 6 characters');
                  return;
                }

                setSignUpError(null);
                try {
                  await handleSignUp(email, password);
                } catch (error: any) {
                  setSignUpError(error.message || 'Sign up failed');
                }
              }}
              className="space-y-4"
            >
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-foreground mb-2">
                  Email
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  className="w-full px-3 py-2 border border-input rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent"
                  placeholder="Enter your email"
                />
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-foreground mb-2">
                  Password
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  required
                  minLength={6}
                  className="w-full px-3 py-2 border border-input rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent"
                  placeholder="Create a password (min 6 characters)"
                />
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-foreground mb-2">
                  Confirm Password
                </label>
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  required
                  minLength={6}
                  className="w-full px-3 py-2 border border-input rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent"
                  placeholder="Confirm your password"
                />
              </div>

              <button
                type="submit"
                className="w-full py-2 px-4 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary-dark shadow-md hover:shadow-lg transition-opacity"
              >
                Create Account
              </button>
            </form>

            <div className="mt-4 text-center">
              <button
                type="button"
                onClick={() => {
                  const newUrl = new URL(window.location.href);
                  newUrl.searchParams.set('action', 'login');
                  window.location.href = newUrl.toString();
                }}
                className="text-sm text-muted-foreground hover:text-foreground"
              >
                Already have an account? Sign in
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Default to login
  return <AuthComponent onAuthSuccess={onAuthSuccess} />;
}
